/**
 * 클라이언트 전역 에러 리포터 (2026-07-15)
 *
 * Vue 렌더링 에러 / window 전역 에러 / 처리 안 된 Promise 거부를 잡아
 * 서버 /api/client-error/ 로 전송한다. 서버가 텔레그램으로 전달.
 *
 * 스팸 방지:
 *  - 같은 에러(메시지+위치)는 5분에 1회만 전송
 *  - 세션(페이지 수명)당 최대 15건
 *  - axios(HTTP) 에러는 제외 — 5xx는 서버 로그에서 이미 텔레그램으로 감
 *  - 무해한 브라우저 잡음(ResizeObserver 등) 제외
 */

const API_BASE = process.env.VUE_APP_API_BASE_URL || "/api";
const THROTTLE_MS = 5 * 60 * 1000;
const MAX_PER_SESSION = 15;

const sentAt = new Map(); // signature -> timestamp
let sessionCount = 0;

const IGNORE_PATTERNS = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Loading chunk \d+ failed/i, // 배포 직후 옛 청크 — SW 업데이트로 해소됨
  /Loading CSS chunk/i,
  /Network Error/i,
];

function getUserName() {
  // JWT payload 에서 사용자 식별 (Authorization 헤더 없이도 누가 겪었는지 파악)
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return String(payload.username || payload.name || payload.user_id || "");
  } catch (e) {
    return "";
  }
}

function shouldSkip(message, err) {
  if (!message) return true;
  // axios/HTTP 에러 제외 (서버측 5xx는 서버가 직접 알림)
  if (err && (err.isAxiosError || (err.config && err.config.url))) return true;
  return IGNORE_PATTERNS.some((re) => re.test(message));
}

function report(payload) {
  const sig = `${payload.message || ""}|${payload.route || ""}`.slice(0, 300);
  const now = Date.now();
  const last = sentAt.get(sig);
  if (last && now - last < THROTTLE_MS) return;
  if (sessionCount >= MAX_PER_SESSION) return;
  sentAt.set(sig, now);
  sessionCount += 1;

  try {
    fetch(`${API_BASE}/client-error/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true, // 페이지 이탈 중에도 전송 시도
      body: JSON.stringify({
        ...payload,
        user: getUserName(),
        href: location.href.slice(0, 300),
      }),
    }).catch(() => {});
  } catch (e) {
    /* 리포트 실패는 무시 — 앱 동작에 영향 금지 */
  }
}

export function installErrorReporter(app, router) {
  const currentRoute = () =>
    (router && router.currentRoute && router.currentRoute.value
      ? router.currentRoute.value.fullPath
      : location.pathname);

  // 1) Vue 컴포넌트 렌더링/핸들러 에러
  const prevHandler = app.config.errorHandler;
  app.config.errorHandler = (err, instance, info) => {
    const message = (err && err.message) || String(err);
    if (!shouldSkip(message, err)) {
      report({
        source: "vue-error",
        message: message.slice(0, 600),
        stack: ((err && err.stack) || "").slice(0, 2500),
        component:
          (instance && instance.$options && (instance.$options.name || instance.$options.__file)) || "",
        route: currentRoute(),
        info: String(info || "").slice(0, 100),
      });
    }
    // 기존 동작 유지: 콘솔에도 출력
    if (prevHandler) prevHandler(err, instance, info);
    else console.error(err);
  };

  // 2) 전역 스크립트 에러
  window.addEventListener("error", (event) => {
    // 리소스 로드 실패(img/script 태그)는 message 없음 — 제외
    if (!event.message) return;
    if (shouldSkip(event.message, event.error)) return;
    report({
      source: "window-error",
      message: String(event.message).slice(0, 600),
      stack: ((event.error && event.error.stack) || `${event.filename}:${event.lineno}:${event.colno}`).slice(0, 2500),
      route: currentRoute(),
    });
  });

  // 3) 처리 안 된 Promise 거부
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = (reason && reason.message) || String(reason);
    if (shouldSkip(message, reason)) return;
    report({
      source: "promise",
      message: String(message).slice(0, 600),
      stack: ((reason && reason.stack) || "").slice(0, 2500),
      route: currentRoute(),
    });
  });
}
